import {
  ChartDataRef,
  QSFFilterRef,
  ProductFilterRef,
  ProductSupplierRef,
  getCountryByCompany,
  getSupplierRef,
} from './Liveboard.service'
import { getAllInspectors } from '../QSF/Inspection/Inspection.service'
import {
  transformAreaChart,
  transformBarChart,
  transformColumnAndLineChart,
  transformColumnChart,
  transformDoughnutChart,
  transformGaugeChart,
  transformHundredPercentStackedColumnChart,
  transformHundredPercentStakeBarChart,
  transformLineChart,
  transformPieChart,
  transformTwoBarChart,
  transformTwoColumnChart,
} from './Liveboard.util'
import { isEmptyValue } from '@/utils/common'

export async function getProductsFilter(companyId: string, filtersConfig: any[] = []) {
  if (!filtersConfig || filtersConfig.length === 0) return []

  const promises = filtersConfig.map(async (filter) => {
    try {
      if (filter.key === 'supplier') {
        const response = await ProductSupplierRef()
        const optionsData = response?.data?.data || []
        return {
          key: filter.key,
          name: filter.name,
          slug: filter.slug || filter.key,
          options: optionsData.map((item) => ({
            name: item?.name || item?.supplier_name || item,
            key: item?.id || item?.supplier_id || item,
          })),
        }
      }

      const configOptions = filter.options

      if (Array.isArray(configOptions) && configOptions.length > 0) {
        return {
          key: filter.key,
          name: filter.name,
          slug: filter.slug || filter.key,
          options: configOptions.map((opt: any) => ({
            name: opt?.label || opt?.value,
            key: opt?.value,
          })),
        }
      }

      const response = await ProductFilterRef(filter.key)
      const optionsData = response?.data?.data || []
      return {
        key: filter.key,
        name: filter.name,
        slug: filter.slug || filter.key,
        options: optionsData.map((item) => ({
          name: item?.value,
          key: item?.value,
        })),
      }
    } catch (error) {
      return {
        key: filter.key,
        name: filter.name,
        slug: filter.slug || filter.key,
        options: [],
      }
    }
  })

  return await Promise.all(promises)
}

export async function getQSFFilter(companyId: string) {
  const data = await QSFFilterRef()
  const tmp = data?.data?.data
    ?.map((item) => {
      return {
        key: item?.name,
        name: item?.name,
        slug: item?.item,
        options: item?.property_values
          ?.map((tmp) => {
            const customProperties = ['inspectionType']
            const isCustomProperty = customProperties.includes(item?.name)

            return {
              name: tmp?.value,
              key: isCustomProperty ? tmp?.id : tmp?.value,
            }
          })
          ?.sort((a, b) => {
            if (a.name?.toLowerCase() > b.name?.toLowerCase()) return 1
            if (a.name?.toLowerCase() < b.name?.toLowerCase()) return -1
            return 0
          }),
      }
    })
    ?.filter((item) => item.name != 'country')
    ?.filter((item) => item.name != 'supplier')
    ?.filter((item) => item.name != 'factory')
  // const taskData = await TaskFilterData()
  const countryByCompany = await getCountryByCompany()
  const supplierData = await getSupplierRef(companyId)
  const supplierFactoryData = await convertSupplierFactoryFilter(
    supplierData?.data.data
  )
  const inspectorData = await getAllInspectors()

  let _data = [...tmp]
  _data.push({
    key: 'country',
    name: 'country',
    slug: 'country',
    options: countryByCompany.data?.data
      .map((item) => {
        return {
          name: item?.name,
          key: item?.id,
        }
      })
      ?.sort((a, b) => {
        if (a.name?.toLowerCase() > b.name?.toLowerCase()) return 1
        if (a.name?.toLowerCase() < b.name?.toLowerCase()) return -1
        return 0
      }),
  })

  _data = _data.filter((item) => {
    return item != null
  })
  // _data.push(taskData?.data?.data)
  _data.push(
    supplierFactoryData.supplierOption,
    supplierFactoryData.factoryOption
  )
  _data.push({
    key: 'inspector',
    name: 'inspector',
    slug: 'inspector',
    options: (inspectorData ?? [])
      ?.map((item) => {
        return {
          name: `${item.first_name} ${item.last_name}`,
          key: item.id,
        }
      })
      ?.sort((a, b) => {
        if (a.name?.toLowerCase() > b.name?.toLowerCase()) return 1
        if (a.name?.toLowerCase() < b.name?.toLowerCase()) return -1
        return 0
      }),
  })
  return _data
}

async function convertSupplierFactoryFilter(data) {
  const factory = {
    key: 'factory',
    name: 'factory',
    slug: 'factory',
    options: [],
  }
  const supplier = {
    key: 'supplier',
    name: 'supplier',
    slug: 'supplier',
    options: [],
  }
  data.map((item) => {
    if (!isEmptyValue(item.factories.length)) {
      item.factories.map((e) => {
        factory.options.push({
          name: e.name,
          key: e.id,
        })
      })
    }
    if (item.is_factory) {
      factory.options.push({
        name: item.name,
        key: item.id,
      })
      supplier.options.push({
        name: item.name,
        key: item.id,
      })
    } else {
      supplier.options.push({
        name: item.name,
        key: item.id,
      })
    }
  })
  supplier?.options?.sort((a, b) => {
    if (a.name?.toLowerCase() > b.name?.toLowerCase()) return 1
    if (a.name?.toLowerCase() < b.name?.toLowerCase()) return -1
    return 0
  })
  factory?.options?.sort((a, b) => {
    if (a.name?.toLowerCase() > b.name?.toLowerCase()) return 1
    if (a.name?.toLowerCase() < b.name?.toLowerCase()) return -1
    return 0
  })
  return await {
    supplierOption: supplier,
    factoryOption: factory,
  }
}

function convertFilterOption(data) {
  switch (data.field) {
    case 'inspectionType':
      data.value = JSON.parse(data.value).name
      return data
    default:
      return data
  }
}

export async function getChartData(
  chartItem: any,
  key: string,
  options: any,
  currentPeriod: string
) {
  const data = await ChartDataRef(key, chartItem, options, currentPeriod)

  switch (chartItem.chart_type) {
    case 'two_column':
      return transformTwoColumnChart(data, chartItem)
    case 'line':
      return transformLineChart(data, chartItem)
    case 'hundred_percent_stacked_column':
      return transformHundredPercentStackedColumnChart(data, chartItem)
    case 'column':
      return transformColumnChart(data, chartItem)
    case 'column_and_line':
      return transformColumnAndLineChart(data, chartItem)
    case 'bar':
      return transformBarChart(data, chartItem)
    case 'gauge':
      return transformGaugeChart(data, chartItem)
    case 'area':
      return transformAreaChart(data, chartItem)
    case 'hundred_percent_stacked_bar':
      return transformHundredPercentStakeBarChart(data, chartItem)
    case 'pie':
      return transformPieChart(data, chartItem)
    case 'doughnut':
      return transformDoughnutChart(data, chartItem)
    case 'two_bar':
      return transformTwoBarChart(data, chartItem)
    case 'multi':
      return data
    default:
      return null
  }
}
